/* eslint-disable react-hooks/exhaustive-deps */
import { Modal } from 'antd';
import clsx from 'clsx';
import {
    AlertCircle,
    ArrowRight,
    Building2,
    CheckCircle2,
    Delete,
    HelpCircle,
    KeyRound,
    ShieldCheck,
    UserCheck
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerDetail, verifyCustomerSecurityKey } from '../../../redux/actions/customers';
import { getRoutes } from '../../../routes/Routes';
import { formatCurrency } from '../../../utils';
import { useKioskIdentity } from '../useKioskIdentity';

export const SECURITY_CODE_LENGTH = 5;

const KioskAccount = () => {
    const navigate = useNavigate();
    useKioskIdentity();
    const [code, setCode] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [isShaking, setIsShaking] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [customerData, setCustomerData] = useState(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const inactivityTimeoutRef = useRef(null);

    // Session Inactivity Auto-Reset (60 seconds)
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }
        inactivityTimeoutRef.current = setTimeout(() => {
            if (isVerified || code.length > 0) {
                setCode('');
                setIsVerified(false);
                setIsLoadingDetails(false);
                setCustomerData(null);
                setErrorMsg('');
            }
        }, 60000);
    }, [isVerified, code]);

    useEffect(() => {
        const handleUserInteraction = () => resetInactivityTimer();
        window.addEventListener('touchstart', handleUserInteraction);
        window.addEventListener('mousedown', handleUserInteraction);

        resetInactivityTimer();
        return () => {
            window.removeEventListener('touchstart', handleUserInteraction);
            window.removeEventListener('mousedown', handleUserInteraction);
            if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
        };
    }, [resetInactivityTimer]);

    // Physical Keyboard & Paste Support
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Don't capture keyboard if verified or currently verifying or modal open
            if (isVerified || isVerifying || isLoadingDetails || isHelpOpen) return;

            // Ignore special modifier keys like Ctrl, Alt, Meta
            if (e.ctrlKey || e.altKey || e.metaKey) return;

            if (e.key === 'Backspace') {
                e.preventDefault();
                setCode((prev) => prev.slice(0, -1));
                setErrorMsg('');
                resetInactivityTimer();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (code.length === SECURITY_CODE_LENGTH) {
                    triggerVerification(code);
                } else {
                    triggerError(`Please enter all ${SECURITY_CODE_LENGTH} characters.`);
                }
                resetInactivityTimer();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setCode('');
                setErrorMsg('');
                resetInactivityTimer();
            } else if (/^[a-zA-Z0-9]$/.test(e.key)) {
                e.preventDefault();
                if (code.length < SECURITY_CODE_LENGTH) {
                    const nextChar = e.key.toUpperCase();
                    const nextCode = code + nextChar;
                    setCode(nextCode);
                    setErrorMsg('');
                    if (nextCode.length === SECURITY_CODE_LENGTH) {
                        triggerVerification(nextCode);
                    }
                }
                resetInactivityTimer();
            }
        };

        const handlePaste = (e) => {
            if (isVerified || isVerifying || isLoadingDetails || isHelpOpen) return;
            const pastedText = (e.clipboardData || window.clipboardData)
                ?.getData('text')
                ?.replace(/[^a-zA-Z0-9]/g, '')
                ?.slice(0, SECURITY_CODE_LENGTH)
                ?.toUpperCase();

            if (pastedText) {
                e.preventDefault();
                setCode(pastedText);
                setErrorMsg('');
                if (pastedText.length === SECURITY_CODE_LENGTH) {
                    triggerVerification(pastedText);
                }
                resetInactivityTimer();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('paste', handlePaste);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('paste', handlePaste);
        };
    }, [code, isVerified, isVerifying, isLoadingDetails, isHelpOpen, resetInactivityTimer]);

    // Keypad Handlers
    const handleAddChar = (char) => {
        if (code.length < SECURITY_CODE_LENGTH) {
            const nextCode = (code + char).toUpperCase();
            setCode(nextCode);
            setErrorMsg('');
            if (nextCode.length === SECURITY_CODE_LENGTH) {
                triggerVerification(nextCode);
            }
        }
    };

    const handleDelete = () => {
        setCode((prev) => prev.slice(0, -1));
        setErrorMsg('');
    };

    const handleClear = () => {
        setCode('');
        setErrorMsg('');
    };

    const triggerError = (msg) => {
        setErrorMsg(msg);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
    };

    // Verification Logic
    const triggerVerification = async (codeToVerify = code) => {
        if (codeToVerify.length < SECURITY_CODE_LENGTH) {
            triggerError(`Please enter all ${SECURITY_CODE_LENGTH} characters.`);
            return;
        }

        setIsVerifying(true);
        setErrorMsg('');

        try {
            const upper = codeToVerify.trim().toUpperCase();
            const res = await verifyCustomerSecurityKey(upper);

            if (res?.ack) {
                const initialCustomer = res?.customer_details || null;
                setCustomerData(initialCustomer);
                setIsVerified(true);

                if (initialCustomer?.customer_netsuite_id) {
                    setIsLoadingDetails(true);
                    try {
                        const resp = await getCustomerDetail(initialCustomer.customer_netsuite_id, false);
                        if (resp?.ack) {
                            const balance = resp?.customer_balance || {};
                            const details = Array.isArray(resp?.customer_details)
                                ? resp?.customer_details?.[0]
                                : resp?.customer_details;

                            const creditLimit = Number(balance?.creditLimit) || 0;
                            const currentBalance = Number(balance?.balance ?? balance?.currentBalance) || 0;
                            const availableCredit = Math.max(0, creditLimit - currentBalance);

                            setCustomerData((prev) => ({
                                ...prev,
                                ...details,
                                ...balance,
                                creditLimit,
                                currentBalance,
                                availableCredit,
                            }));
                        }
                    } catch (detailsErr) {
                        console.error('Failed to load customer details:', detailsErr);
                    } finally {
                        setIsLoadingDetails(false);
                    }
                }
            } else {
                triggerError('Invalid security code. Please check and try again.');
                setCode('');
            }
        } catch (err) {
            triggerError('Unable to verify security code. Please contact store staff.');
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResetSession = () => {
        setCode('');
        setIsVerified(false);
        setIsLoadingDetails(false);
        setCustomerData(null);
        setErrorMsg('');
    };

    const handleRedirectToKiosk = () => {
        navigate(
            getRoutes().kiosk.route(),
            {
                state: {
                    customer: customerData,
                },
            }
        );
    };

    return (
        <div className="kiosk-account-wrapper">
            {/* Ambient background visual glows */}
            <div className="kiosk-ambient-decor-1" />
            <div className="kiosk-ambient-decor-2" />

            {/* Main Content Area */}
            <main className="kiosk-account-main">
                {!isVerified ? (
                    <div className={clsx('kiosk-verify-card', { 'shake-error': isShaking })}>
                        {/* Hero Icon */}
                        <div className="verify-icon-hero">
                            <div className="icon-ambient-glow" />
                            <div className="icon-shield-bubble">
                                <ShieldCheck size={36} strokeWidth={2.2} />
                            </div>
                        </div>

                        <div className="verify-typography">
                            <h1 className="verify-title">Account Verification</h1>
                            <p className="verify-desc">
                                Enter your <strong>{SECURITY_CODE_LENGTH} character security passcode</strong> to authenticate your account, check balances, and unlock personalized prices.
                            </p>
                        </div>

                        {/* Code Slots Container */}
                        <div className="verify-input-section">
                            <div className="kiosk-code-boxes">
                                {Array.from({ length: SECURITY_CODE_LENGTH }).map((_, idx) => {
                                    const char = code[idx];
                                    const isCurrent = idx === code.length && !isVerifying;
                                    const isFilled = char !== undefined;

                                    return (
                                        <div
                                            key={idx}
                                            className={clsx('kiosk-code-slot', {
                                                active: isCurrent,
                                                filled: isFilled,
                                                error: !!errorMsg,
                                                success: isVerified,
                                            })}
                                        >
                                            {isFilled ? (
                                                char
                                            ) : isCurrent ? (
                                                <span className="kiosk-cursor-bar" />
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Error Message Banner */}
                        {errorMsg && (
                            <div className="verify-status-banner banner-error">
                                <AlertCircle size={17} className="banner-icon" />
                                <span>{errorMsg}</span>
                            </div>
                        )}

                        {/* Kiosk Touch Alphanumeric Keyboard (Numbers + Letters) */}
                        <div className="kiosk-alphanumeric-keyboard">
                            {/* Row 1: Numbers */}
                            <div className="keyboard-row">
                                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map((num) => (
                                    <button
                                        key={num}
                                        type="button"
                                        className="keyboard-key key-number"
                                        disabled={isVerifying}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddChar(num);
                                        }}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>

                            {/* Row 2: Letters Q-P */}
                            <div className="keyboard-row">
                                {['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'].map((letter) => (
                                    <button
                                        key={letter}
                                        type="button"
                                        className="keyboard-key"
                                        disabled={isVerifying}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddChar(letter);
                                        }}
                                    >
                                        {letter}
                                    </button>
                                ))}
                            </div>

                            {/* Row 3: Letters A-L */}
                            <div className="keyboard-row">
                                {['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'].map((letter) => (
                                    <button
                                        key={letter}
                                        type="button"
                                        className="keyboard-key"
                                        disabled={isVerifying}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddChar(letter);
                                        }}
                                    >
                                        {letter}
                                    </button>
                                ))}
                            </div>

                            {/* Row 4: CLEAR, Letters Z-M, DELETE */}
                            <div className="keyboard-row">
                                <button
                                    type="button"
                                    className="keyboard-key key-action"
                                    disabled={isVerifying || code.length === 0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClear();
                                    }}
                                >
                                    CLEAR
                                </button>

                                {['Z', 'X', 'C', 'V', 'B', 'N', 'M'].map((letter) => (
                                    <button
                                        key={letter}
                                        type="button"
                                        className="keyboard-key"
                                        disabled={isVerifying}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddChar(letter);
                                        }}
                                    >
                                        {letter}
                                    </button>
                                ))}

                                <button
                                    type="button"
                                    className="keyboard-key key-action key-delete"
                                    disabled={isVerifying || code.length === 0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete();
                                    }}
                                >
                                    <Delete size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Primary Action Button */}
                        <button
                            type="button"
                            className="verify-cta-button"
                            disabled={isVerifying || code.length < SECURITY_CODE_LENGTH}
                            onClick={(e) => {
                                e.stopPropagation();
                                triggerVerification(code);
                            }}
                        >
                            {isVerifying ? (
                                <>
                                    <span>Verifying Passcode...</span>
                                </>
                            ) : (
                                <>
                                    <ShieldCheck size={20} />
                                    <span>Verify Account</span>
                                </>
                            )}
                        </button>

                        {/* Footer Quick Links */}
                        <div className="verify-card-footer">
                            <button
                                type="button"
                                className="footer-action-link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsHelpOpen(true);
                                }}
                            >
                                <HelpCircle size={14} />
                                <span>Where do I find my code?</span>
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ------------------------------------------------------------- */
                    /* VERIFIED CUSTOMER ACCOUNT DASHBOARD CARD                      */
                    /* ------------------------------------------------------------- */
                    <div className="kiosk-verified-dashboard">
                        {/* Header */}
                        <div className="verified-header-bar">
                            <div className="verified-profile">
                                <div className="avatar-circle">
                                    <Building2 size={24} />
                                </div>
                                <div className="customer-meta">
                                    <h2 className="customer-name">{customerData?.title}</h2>
                                    <div className="customer-id-pill">
                                        <UserCheck size={13} className="text-green-600" />
                                        <span>CODE: {customerData?.code}</span>
                                        <span>•</span>
                                        <span className="text-orange-600 font-semibold">{customerData?.phone || customerData?.invoice_email}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="verified-badge-pill">
                                <CheckCircle2 size={16} />
                                <span>Authenticated</span>
                            </div>
                        </div>

                        {/* Financial Overview Metrics */}
                        <div className="flex flex-column gap-3">
                            <div className="verified-finance-grid">
                                <div className="finance-metric-card">
                                    <span className="metric-label">Credit Limit</span>
                                    <span className="metric-value text-blue">
                                        {isLoadingDetails ? (
                                            <span className="kiosk-skeleton-shimmer kiosk-skeleton-value" />
                                        ) : (
                                            <span className="kiosk-value-reveal">{formatCurrency(customerData?.creditLimit || 0)}</span>
                                        )}
                                    </span>
                                    <span className="metric-sub">
                                        {isLoadingDetails ? (
                                            <span className="kiosk-skeleton-shimmer kiosk-skeleton-sub" />
                                        ) : (
                                            'Approved Terms'
                                        )}
                                    </span>
                                </div>

                                <div className="finance-metric-card">
                                    <span className="metric-label">Current Balance</span>
                                    <span className="metric-value text-orange">
                                        {isLoadingDetails ? (
                                            <span className="kiosk-skeleton-shimmer kiosk-skeleton-value" />
                                        ) : (
                                            <span className="kiosk-value-reveal">{formatCurrency(customerData?.currentBalance || 0)}</span>
                                        )}
                                    </span>
                                    <span className="metric-sub">
                                        {isLoadingDetails ? (
                                            <span className="kiosk-skeleton-shimmer kiosk-skeleton-sub" />
                                        ) : (
                                            'Last sync just now'
                                        )}
                                    </span>
                                </div>

                                <div className="finance-metric-card">
                                    <span className="metric-label">Available Credit</span>
                                    <span className="metric-value text-green">
                                        {isLoadingDetails ? (
                                            <span className="kiosk-skeleton-shimmer kiosk-skeleton-value" />
                                        ) : (
                                            <span className="kiosk-value-reveal">{formatCurrency(customerData?.availableCredit || 0)}</span>
                                        )}
                                    </span>
                                    <span className="metric-sub">
                                        {isLoadingDetails ? (
                                            <span className="kiosk-skeleton-shimmer kiosk-skeleton-sub" />
                                        ) : (
                                            'Ready for ordering'
                                        )}
                                    </span>
                                </div>
                            </div>

                            {/* Credit Usage Gauge */}
                            <div className="credit-usage-box">
                                <div className="usage-header">
                                    <span>Credit Utilization</span>
                                    <span>
                                        {isLoadingDetails ? (
                                            <span className="kiosk-skeleton-shimmer kiosk-skeleton-badge" />
                                        ) : (
                                            <span className="kiosk-value-reveal">
                                                {`${Math.round(
                                                    ((customerData?.currentBalance || 0) / (customerData?.creditLimit || 1)) * 100
                                                )}% Used`}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="usage-track">
                                    <div
                                        className={clsx('usage-fill', { 'is-loading': isLoadingDetails })}
                                        style={{
                                            width: isLoadingDetails
                                                ? '100%'
                                                : `${Math.min(
                                                    100,
                                                    ((customerData?.currentBalance || 0) / (customerData?.creditLimit || 1)) * 100
                                                )}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="verified-actions-row">
                            <button
                                type="button"
                                className="btn-kiosk-start"
                                disabled={isLoadingDetails}
                                onClick={handleRedirectToKiosk}
                            >
                                {isLoadingDetails ? (
                                    <>
                                        <span>Syncing Account Data...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Proceed to Kiosk Store</span>
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                className="btn-kiosk-exit"
                                onClick={handleResetSession}
                            >
                                <span>Switch Account</span>
                            </button>
                        </div>
                    </div>
                )}
            </main>

            {/* Help & Passcode Guide Modal */}
            <Modal
                open={isHelpOpen}
                onCancel={() => setIsHelpOpen(false)}
                footer={null}
                centered
                width={540}
                styles={{
                    content: {
                        borderRadius: '24px',
                        padding: '32px 28px',
                    },
                }}
            >
                <div className="flex flex-column gap-4">
                    <div className="flex align-items-center gap-3">
                        <div
                            className="flex align-items-center justify-content-center border-round-2xl"
                            style={{
                                width: 48,
                                height: 48,
                                background: '#fff7ed',
                                color: '#ea580c',
                                border: '1.5px solid #fed7aa',
                                flexShrink: 0,
                            }}
                        >
                            <KeyRound size={24} />
                        </div>
                        <div>
                            <div className="font-bold text-gray-900 text-lg" style={{ lineHeight: 1.25 }}>
                                Security Passcode Guide
                            </div>
                            <div className="text-xs text-gray-500 mt-1 font-medium">
                                Self-Service Kiosk Customer Authentication
                            </div>
                        </div>
                    </div>

                    <div
                        className="border-round-2xl p-4"
                        style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            fontSize: '14.5px',
                            lineHeight: 1.6,
                            color: '#475569',
                        }}
                    >
                        <div className="font-bold text-gray-800 text-base mb-3">
                            How to get your passcode:
                        </div>
                        <ul className="m-0 pl-3 flex flex-column gap-3">
                            <li>
                                <strong>Sales Representative:</strong> Your dedicated Magna sales rep can generate or share your active {SECURITY_CODE_LENGTH} character kiosk passcode.
                            </li>
                            <li>
                                <strong>Store Counter:</strong> Speak to any Magna counter staff or store associate for instant account verification.
                            </li>
                        </ul>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default KioskAccount;